import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import BPeSeatMap from "./BPeSeatMap";

function asInt(v) {
  const n = typeof v === "number" ? v : parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
}

function formatSeatLabel(n) {
  return String(n).padStart(2, "0");
}

/**
 * Passo de poltronas — mapa em destaque; demais recursos em painel recolhível.
 */
export default function BPeSeatStep({
  loading,
  quantidadePassageiros,
  assentos,
  activeSeatPassenger,
  onActivePassengerChange,
  onSelectSeat,
  t,
}) {
  const seatInputRef = useRef(null);
  const [seatInput, setSeatInput] = useState("");
  const [seatInputError, setSeatInputError] = useState("");
  const [extrasOpen, setExtrasOpen] = useState(false);

  const selectedCount = useMemo(() => {
    let n = 0;
    for (let i = 0; i < quantidadePassageiros; i++) {
      if (assentos.selected[i] != null && assentos.selected[i] !== "") n += 1;
    }
    return n;
  }, [assentos.selected, quantidadePassageiros]);

  const availableList = useMemo(() => {
    const taken = new Set();
    Object.values(assentos.selected || {}).forEach((s) => {
      const n = asInt(s);
      if (n != null) taken.add(String(n));
    });
    return (assentos.disponiveis || [])
      .map((v) => asInt(v))
      .filter((n) => n != null && !taken.has(String(n)))
      .sort((a, b) => a - b);
  }, [assentos.disponiveis, assentos.selected]);

  const allDone = selectedCount === quantidadePassageiros;
  const activeSeat = assentos.selected[activeSeatPassenger];
  const activeHasSeat = activeSeat != null && activeSeat !== "";
  const multiPassenger = quantidadePassageiros > 1;
  const showQuickList = availableList.length > 0 && availableList.length <= 48;

  const statusLine = useMemo(() => {
    if (allDone) return t("bpeWizard.seatsAllDone");
    if (multiPassenger) {
      if (activeHasSeat) {
        return t("bpeWizard.seatsActionBannerDone", {
          n: activeSeatPassenger + 1,
          seat: formatSeatLabel(activeSeat),
        });
      }
      return t("bpeWizard.seatsActionBanner", { n: activeSeatPassenger + 1 });
    }
    if (activeHasSeat) {
      return t("bpeWizard.seatSelected", { seat: formatSeatLabel(activeSeat) });
    }
    return t("bpeWizard.seatsStepHintSingle");
  }, [allDone, multiPassenger, activeHasSeat, activeSeat, activeSeatPassenger, t]);

  const tryApplySeatInput = () => {
    const raw = seatInput.trim();
    if (!raw) return;
    const n = asInt(raw);
    if (n == null) {
      setSeatInputError(t("bpeWizard.seatsTypeNumberInvalid", { seat: raw }));
      return;
    }
    const isAvailable = availableList.some((v) => String(v) === String(n));
    const isCurrent = String(activeSeat) === String(n);
    if (!isAvailable && !isCurrent) {
      setSeatInputError(t("bpeWizard.seatsTypeNumberInvalid", { seat: n }));
      return;
    }
    setSeatInputError("");
    setSeatInput("");
    onSelectSeat(activeSeatPassenger, n);
    seatInputRef.current?.focus();
  };

  const handleClearActiveSeat = () => {
    if (activeHasSeat) onSelectSeat(activeSeatPassenger, activeSeat);
    setSeatInput("");
    setSeatInputError("");
  };

  if (loading) {
    return (
      <Stack alignItems="center" spacing={1} py={3} sx={{ flex: 1, justifyContent: "center" }}>
        <LinearProgress sx={{ width: "100%", maxWidth: 280, height: 4, borderRadius: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {t("bpeWizard.seatsLoading")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1, gap: 0.75 }}>
      {multiPassenger && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} role="tablist" aria-label={t("bpeWizard.selectSeats")}>
          {Array.from({ length: quantidadePassageiros }, (_, i) => {
            const seat = assentos.selected[i];
            const isActive = activeSeatPassenger === i;
            const isDone = seat != null && seat !== "";
            const label = isDone
              ? t("bpeWizard.seatsSummaryChip", {
                  passenger: i + 1,
                  seat: formatSeatLabel(seat),
                })
              : t("bpeWizard.passengerN", { n: i + 1 });
            return (
              <Chip
                key={i}
                size="small"
                role="tab"
                aria-selected={isActive}
                icon={isDone ? <CheckCircleIcon /> : undefined}
                label={label}
                clickable
                color={isActive ? "primary" : isDone ? "success" : "default"}
                variant={isActive ? "filled" : "outlined"}
                onClick={() => onActivePassengerChange(i)}
                sx={{ height: 26, fontWeight: 700 }}
              />
            );
          })}
        </Stack>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column" }}>
        <BPeSeatMap
          seats={assentos.grid}
          availableSeatNumbers={assentos.disponiveis}
          selectedByPassenger={assentos.selected}
          activePassengerIndex={activeSeatPassenger}
          passengerCount={quantidadePassageiros}
          onSelectSeat={onSelectSeat}
          compactHeader
        />
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flexShrink: 0 }}>
        <Typography
          variant="caption"
          color={allDone ? "success.main" : "text.secondary"}
          fontWeight={600}
          lineHeight={1.3}
        >
          {statusLine}
        </Typography>
        {activeHasSeat && !allDone && (
          <Button
            size="small"
            color="inherit"
            onClick={handleClearActiveSeat}
            startIcon={<ClearIcon sx={{ fontSize: "14px !important" }} />}
            sx={{ textTransform: "none", minWidth: 0, px: 0.5, fontSize: "0.72rem", flexShrink: 0 }}
          >
            {t("bpeWizard.seatsClearCurrent")}
          </Button>
        )}
      </Stack>

      <Box sx={{ flexShrink: 0 }}>
        <Button
          size="small"
          color="inherit"
          startIcon={<TuneIcon sx={{ fontSize: "16px !important" }} />}
          endIcon={extrasOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setExtrasOpen((o) => !o)}
          sx={{ textTransform: "none", py: 0, px: 0.5, fontSize: "0.75rem", color: "text.secondary" }}
        >
          {t("bpeWizard.seatsMoreOptions")}
        </Button>
        <Collapse in={extrasOpen}>
          <Stack spacing={1} sx={{ pt: 0.75, pb: 0.25 }}>
            <Stack direction="row" spacing={0.75} alignItems="flex-start">
              <TextField
                inputRef={seatInputRef}
                size="small"
                label={t("bpeWizard.seatsTypeNumber")}
                placeholder="34"
                value={seatInput}
                onChange={(e) => {
                  setSeatInput(e.target.value.replace(/\D/g, "").slice(0, 3));
                  setSeatInputError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    tryApplySeatInput();
                  }
                }}
                error={Boolean(seatInputError)}
                helperText={seatInputError || t("bpeWizard.seatsTypeNumberHint")}
                disabled={allDone}
                sx={{ flex: 1, minWidth: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EventSeatIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={tryApplySeatInput}
                        disabled={!seatInput.trim() || allDone}
                        aria-label={t("bpeWizard.seatsTypeNumberApply")}
                      >
                        <KeyboardReturnIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={t("bpeWizard.seatsAvailableCount", { count: availableList.length })}
                sx={{ mt: 0.75, height: 24 }}
              />
            </Stack>

            {showQuickList && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                  {t("bpeWizard.seatsQuickList")}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {availableList.map((n) => (
                    <Chip
                      key={n}
                      size="small"
                      label={formatSeatLabel(n)}
                      clickable
                      color={
                        String(assentos.selected[activeSeatPassenger]) === String(n) ? "primary" : "default"
                      }
                      onClick={() => onSelectSeat(activeSeatPassenger, n)}
                      sx={{ fontWeight: 700, minWidth: 44, height: 24 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Collapse>
      </Box>

      {availableList.length === 0 && selectedCount < quantidadePassageiros && (
        <Alert severity="warning" sx={{ py: 0, flexShrink: 0 }}>
          {t("bpeWizard.seatsNoAvailable")}
        </Alert>
      )}
    </Box>
  );
}
